import { useMemo } from 'react'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js'
import { Bar } from 'react-chartjs-2'
import type { Theme } from '@/types'
import { formatCurrency } from '@/utils/calculations'
import { getChartTheme, getMutedColor } from '@/utils/chartConfig'

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

interface ComparisonBarChartProps {
  withAdvice: number
  withoutAdvice: number
  primaryColor?: string
  theme?: Theme
  horizontal?: boolean
  height?: number
}

export default function ComparisonBarChart({
  withAdvice,
  withoutAdvice,
  primaryColor = '#14b8a6',
  theme = 'dark',
  horizontal = true,
  height = 150,
}: ComparisonBarChartProps) {
  const chartTheme = useMemo(() => getChartTheme(theme), [theme])
  const mutedColor = useMemo(() => getMutedColor(primaryColor, theme), [primaryColor, theme])

  const data = useMemo(
    () => ({
      labels: ['Portfolio Value'],
      datasets: [
        {
          label: 'With Advice',
          data: [withAdvice],
          backgroundColor: primaryColor,
          borderRadius: 4,
          barThickness: 30,
        },
        {
          label: 'Without Advice',
          data: [withoutAdvice],
          backgroundColor: mutedColor,
          borderRadius: 4,
          barThickness: 30,
        },
      ],
    }),
    [withAdvice, withoutAdvice, primaryColor, mutedColor]
  )

  const options = useMemo(
    () => ({
      indexAxis: horizontal ? ('y' as const) : ('x' as const),
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'top' as const,
          labels: {
            color: chartTheme.textColor,
            usePointStyle: true,
            padding: 20,
            font: {
              family: "'Source Sans 3', sans-serif",
            },
          },
        },
        tooltip: {
          backgroundColor: chartTheme.tooltipBg,
          titleColor: chartTheme.tooltipText,
          bodyColor: chartTheme.tooltipText,
          borderColor: chartTheme.gridColor,
          borderWidth: 1,
          padding: 12,
          callbacks: {
            label: (ctx: { dataset: { label?: string }; raw: unknown }) =>
              `${ctx.dataset.label}: ${formatCurrency(ctx.raw as number)}`,
          },
        },
      },
      scales: {
        x: {
          grid: {
            color: chartTheme.gridColor,
          },
          ticks: {
            color: chartTheme.textColor,
            font: {
              family: "'Source Sans 3', sans-serif",
            },
            callback: horizontal ? (value: string | number) => formatCurrency(value as number) : undefined,
          },
        },
        y: {
          grid: {
            display: false,
          },
          ticks: {
            color: chartTheme.textColor,
            font: {
              family: "'Source Sans 3', sans-serif",
            },
            callback: !horizontal ? (value: string | number) => formatCurrency(value as number) : undefined,
          },
        },
      },
    }),
    [chartTheme, horizontal]
  )

  return (
    <div style={{ height }}>
      <Bar data={data} options={options} />
    </div>
  )
}
